import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllBookings,
  completeInStoreBooking,
  cancelBooking,
} from "../../../api/api";
import { SearchContext } from "../../../context/SearchContext";
import Pagination from "../../../components/layout/Pagination/Pagination";
import styles from "./BookingCustomer.module.css";

const formatPickupOption = (option) => {
  if (!option) return "N/A";

  switch (option) {
    case "pickup":
      return "Pickup";
    case "delivery":
      return "Delivery";
    case "pickup/delivery":
      return "Pickup & Delivery";
    case "in-store":
      return "In-Store";
    case "express-pickup":
      return "Express Pickup";
    case "express-delivery":
      return "Express Delivery";
    case "express-pickup/delivery":
      return "Express Pickup & Delivery";
    default:
      // fallback: safe capitalization
      return option
        .split(/[\s/-]+/)
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
  }
};

const formatTension = (booking) => {
  if (!booking.tension_type) return "—";
  if (booking.tension_type === "support") return "Talk with support";
  if (booking.is_hybrid === 1 || booking.is_hybrid) {
    if (booking.tension_type === "standard") return "Main: Standard, Cross: Standard";
    const unit = booking.tension_unit || "lbs";
    const mainPrestretch = booking.prestretch ? ` (Prestretch: ${booking.prestretch_percent ?? "—"}%)` : "";
    const crossPrestretch = booking.prestretch_cross ? ` (Prestretch: ${booking.prestretch_percent_cross ?? "—"}%)` : "";
    const mainStr = booking.tension_main != null ? `${booking.tension_main} ${unit}${mainPrestretch}` : "Standard";
    const crossStr = booking.tension_cross != null ? `${booking.tension_cross} ${unit}${crossPrestretch}` : "Standard";
    return `Main: ${mainStr}, Cross: ${crossStr}`;
  }
  if (booking.tension_type === "standard") return "Standard";
  if (booking.tension_type === "custom") {
    const unit = booking.tension_unit || "lbs";
    let label = `${booking.tension_main ?? "—"} ${unit} main / ${booking.tension_cross ?? "—"} ${unit} cross`;
    if (booking.prestretch) {
      label += `, Prestretch: ${booking.prestretch_percent ?? "—"}%`;
    }
    return label;
  }
  return "—";
};

const BookingCustomer = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [modalBookings, setModalBookings] = useState([]);
  const [receiptNumber, setReceiptNumber] = useState("");
  const [cashAmount, setCashAmount] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelBookingData, setCancelBookingData] = useState(null);
  const [canceling, setCanceling] = useState(false);

  const [checkedBookings, setCheckedBookings] = useState({});

  const itemsPerPage = 5;
  const { searchTerm } = useContext(SearchContext);

  const fetchBookings = async () => {
    try {
      const response = await getAllBookings();
      const sortedBookings = response.data.sort((a, b) => {
        const statusOrder = { pending: 0, completed: 1, canceled: 2 };
        const statusA = statusOrder[a.status] ?? 3;
        const statusB = statusOrder[b.status] ?? 3;

        if (statusA !== statusB) {
          return statusA - statusB;
        }

        return new Date(b.booking_date) - new Date(a.booking_date);
      });
      setBookings(sortedBookings);
      setLoading(false);
    } catch (err) {
      setError(err.message || "Failed to fetch bookings");
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/login", {
        state: { error: "Session expired. Please log in again." },
      });
      return;
    }
    fetchBookings();
  }, []);

  const filteredBookings = bookings.filter((booking) => {
    const term = (searchTerm || "").toLowerCase();
    const name = (booking.customer_name || "").toLowerCase();
    const email = (booking.customer_email || "").toLowerCase();
    return name.includes(term) || email.includes(term);
  });

  // Group Bookings by cart_id helper
  const groupBookings = (list) => {
    const groups = [];
    const cartMap = {};

    list.forEach((b) => {
      if (b.cart_id) {
        if (!cartMap[b.cart_id]) {
          cartMap[b.cart_id] = {
            cart_id: b.cart_id,
            customer_name: b.customer_name,
            customer_email: b.customer_email,
            customer_phone: b.customer_phone,
            pickup_option: b.pickup_option,
            instore_location: b.instore_location,
            delivery_address: b.delivery_address,
            payment_option: b.payment_option,
            bookings: [],
          };
          groups.push(cartMap[b.cart_id]);
        }
        cartMap[b.cart_id].bookings.push(b);
      } else {
        // Generate a random temporary key for standalone groupings
        const standaloneKey = `standalone-${b.booking_id || b.grip_booking_id}-${b.service_kind}`;
        groups.push({
          cart_id: null,
          standaloneKey,
          customer_name: b.customer_name,
          customer_email: b.customer_email,
          customer_phone: b.customer_phone,
          pickup_option: b.pickup_option,
          instore_location: b.instore_location,
          delivery_address: b.delivery_address,
          payment_option: b.payment_option,
          bookings: [b],
        });
      }
    });

    return groups;
  };

  const groupedBookings = groupBookings(filteredBookings);
  const totalPages = Math.ceil(groupedBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedGroups = groupedBookings.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1);
  }

  const handlePageChange = (page) => setCurrentPage(page);

  const toggleCheck = (booking) => {
    const key = `${booking.booking_id || booking.grip_booking_id}-${booking.service_kind}`;
    setCheckedBookings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleMarkAsComplete = (groupBookings) => {
    setModalBookings(groupBookings);

    // Sum base total
    const baseTotal = groupBookings.reduce(
      (sum, b) => sum + parseFloat(b.total_cost || 0),
      0
    );

    // Calculate cart pickup fee
    const firstBooking = groupBookings[0];
    let pickupFee = 0;
    if (firstBooking.cart_id) {
      const sameCart = bookings.filter((b) => b.cart_id === firstBooking.cart_id);
      const pickupBooking = sameCart.find((b) => parseFloat(b.pickup_fee || 0) > 0);
      if (pickupBooking) {
        if (pickupBooking.service_kind === "grip") {
          pickupFee = parseFloat(pickupBooking.pickup_fee || 0);
        } else {
          const stringBookingsInCart = sameCart.filter((b) => b.service_kind === "string");
          pickupFee = stringBookingsInCart.reduce(
            (sum, b) => sum + parseFloat(b.pickup_fee || 0),
            0
          );
        }
      }
    }

    const defaultAmt = (baseTotal + pickupFee).toFixed(2);

    setReceiptNumber("");
    setCashAmount(defaultAmt);
    setFormError("");
    setShowModal(true);
  };

  const submitInStorePayment = async () => {
    if (!receiptNumber.trim()) {
      setFormError("Odoo receipt number is required.");
      return;
    }
    const amt = parseFloat(cashAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setFormError("Enter a valid positive amount.");
      return;
    }

    setSaving(true);
    try {
      const numBookings = modalBookings.length;
      const totalEstimated = modalBookings.reduce((sum, b) => {
        let cost = parseFloat(b.total_cost || 0);
        cost += parseFloat(b.pickup_fee || 0);
        return sum + cost;
      }, 0);

      // Loop over selected bookings
      for (let i = 0; i < numBookings; i++) {
        const b = modalBookings[i];
        const bookingId = b.booking_id || b.grip_booking_id;
        const isGrip = b.service_kind === "grip";

        // Proportional share calculation
        let individualCost = parseFloat(b.total_cost || 0) + parseFloat(b.pickup_fee || 0);
        let share = amt;
        if (totalEstimated > 0) {
          share = amt * (individualCost / totalEstimated);
        } else {
          share = amt / numBookings;
        }

        let callAmount = parseFloat(share.toFixed(2));
        // Distribute the exact remaining difference to the last element to prevent rounding differences
        if (i === numBookings - 1) {
          const sumOfPrevShares = modalBookings.slice(0, -1).reduce((sum, _, idx) => {
            let prevCost = parseFloat(modalBookings[idx].total_cost || 0) + parseFloat(modalBookings[idx].pickup_fee || 0);
            let prevShare = totalEstimated > 0 ? amt * (prevCost / totalEstimated) : amt / numBookings;
            return sum + parseFloat(prevShare.toFixed(2));
          }, 0);
          callAmount = parseFloat((amt - sumOfPrevShares).toFixed(2));
        }

        // Safety fallback to satisfy backend positive constraint
        if (callAmount <= 0) {
          callAmount = 0.01;
        }

        await completeInStoreBooking(
          bookingId,
          {
            receipt_number: receiptNumber.trim(),
            amount: callAmount,
          },
          isGrip
        );
      }

      const completedIds = modalBookings.map((b) => b.booking_id || b.grip_booking_id);

      setBookings((prev) =>
        prev.map((b) => {
          const bId = b.booking_id || b.grip_booking_id;
          return completedIds.includes(bId)
            ? { ...b, status: "completed", odoo_receipt_number: receiptNumber.trim() }
            : b;
        })
      );

      // Clean check states
      setCheckedBookings((prev) => {
        const copy = { ...prev };
        modalBookings.forEach((b) => {
          const key = `${b.booking_id || b.grip_booking_id}-${b.service_kind}`;
          delete copy[key];
        });
        return copy;
      });

      setShowModal(false);
      setModalBookings([]);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to record in-store payment";
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    if (!saving) {
      setShowModal(false);
      setModalBookings([]);
      setReceiptNumber("");
      setCashAmount("");
      setFormError("");
    }
  };

  const handleCancelBooking = (booking) => {
    setCancelBookingData(booking);
    setShowCancelModal(true);
  };

  const confirmCancelBooking = async () => {
    if (!cancelBookingData) return;

    setCanceling(true);
    try {
      const bookingId =
        cancelBookingData.booking_id || cancelBookingData.grip_booking_id;
      const isGrip = cancelBookingData.service_kind === "grip";

      await cancelBooking(bookingId, isGrip);

      setBookings((prev) =>
        prev.map((b) => {
          const bId = b.booking_id || b.grip_booking_id;
          return bId === bookingId ? { ...b, status: "canceled" } : b;
        })
      );

      setShowCancelModal(false);
      setCancelBookingData(null);
    } catch (err) {
      console.error("Error canceling booking:", err);
      alert(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to cancel booking"
      );
    } finally {
      setCanceling(false);
    }
  };

  const closeCancelModal = () => {
    if (!canceling) {
      setShowCancelModal(false);
      setCancelBookingData(null);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Customer Bookings</h2>

      {loading && <p className={styles.message}>Loading bookings...</p>}
      {error && <p className={styles.error}>{error}</p>}

      {!loading && !error && (
        <div className={styles.bookingList} style={{ gridTemplateColumns: "1fr", display: "flex", flexDirection: "column" }}>
          {displayedGroups.length > 0 ? (
            displayedGroups.map((group) => {
              const activeBookings = group.bookings.filter(
                (b) => b.status !== "completed" && b.status !== "canceled"
              );
              const groupKey = group.cart_id || group.standaloneKey;

              // Check if all active bookings in the group are checked
              const allActiveChecked = activeBookings.every((b) => {
                const key = `${b.booking_id || b.grip_booking_id}-${b.service_kind}`;
                return checkedBookings[key] === true;
              });

              // Compute cart level pickup fee
              let groupPickupFee = 0;
              if (group.cart_id) {
                const pickupBooking = group.bookings.find(
                  (b) => parseFloat(b.pickup_fee || 0) > 0
                );
                if (pickupBooking) {
                  if (pickupBooking.service_kind === "grip") {
                    groupPickupFee = parseFloat(pickupBooking.pickup_fee || 0);
                  } else {
                    const stringBookingsInCart = group.bookings.filter(
                      (b) => b.service_kind === "string"
                    );
                    groupPickupFee = stringBookingsInCart.reduce(
                      (sum, b) => sum + parseFloat(b.pickup_fee || 0),
                      0
                    );
                  }
                }
              }

              return (
                <div key={groupKey} className={styles.groupCard}>
                  <div className={styles.groupHeader}>
                    <div className={styles.customerInfo}>
                      <h3>{group.customer_name}</h3>
                      <div className={styles.customerMeta}>
                        <span>📧 {group.customer_email}</span>
                        {group.customer_phone && <span>📞 {group.customer_phone}</span>}
                      </div>
                    </div>
                    <div>
                      {group.cart_id ? (
                        <span className={styles.cartBadge}>Cart: {group.cart_id}</span>
                      ) : (
                        <span className={`${styles.cartBadge} ${styles.customCart}`}>Manual Order</span>
                      )}
                    </div>
                  </div>

                  <div className={styles.groupOrderMeta}>
                    <div className={styles.metaItem}>
                      <strong>Pickup Option:</strong>{" "}
                      {group.pickup_option === "in-store"
                        ? `${formatPickupOption(group.pickup_option)} (${
                            group.instore_location || "Location N/A"
                          })`
                        : formatPickupOption(group.pickup_option)}
                    </div>
                    {group.delivery_address && (
                      <div className={styles.metaItem}>
                        <strong>Delivery Address:</strong> {group.delivery_address}
                      </div>
                    )}
                    <div className={styles.metaItem}>
                      <strong>Payment Option:</strong>{" "}
                      {group.payment_option === "online" ? (
                        <span className={styles.paid}>Paid Online</span>
                      ) : (
                        <span className={styles.unpaid}>Pay In-Store</span>
                      )}
                    </div>
                    {groupPickupFee > 0 && (
                      <div className={styles.metaItem}>
                        <strong>Pickup Fee (Order):</strong>{" "}
                        <span className="dirham-symbol">&#xea;</span> {groupPickupFee.toFixed(2)}
                      </div>
                    )}
                  </div>

                  <div className={styles.groupBody}>
                    {group.bookings.map((booking) => {
                      const kind =
                        (booking.service_kind || "").charAt(0).toUpperCase() +
                        (booking.service_kind || "").slice(1);

                      const typeWithKind = booking.service_type
                        ? `${booking.service_type} / ${kind}`
                        : kind || "N/A";

                      const uniqueKey = `${booking.booking_id || booking.grip_booking_id}-${booking.service_kind}`;
                      const isBookingActive =
                        booking.status !== "completed" && booking.status !== "canceled";

                      return (
                        <div
                          key={uniqueKey}
                          className={`${styles.nestedBookingItem} ${
                            checkedBookings[uniqueKey] ? styles.checked : ""
                          }`}
                        >
                          {group.bookings.length > 1 && isBookingActive && (
                            <div className={styles.checkboxContainer}>
                              <input
                                type="checkbox"
                                className={styles.bookingCheckbox}
                                checked={checkedBookings[uniqueKey] || false}
                                onChange={() => toggleCheck(booking)}
                              />
                            </div>
                          )}

                          <div className={styles.bookingContent}>
                            <div className={styles.bookingMetaRow}>
                              <span className={styles.bookingTitle}>
                                #{booking.booking_id || booking.grip_booking_id} —{" "}
                                {booking.racket_name || "Racket"}
                              </span>
                              <span
                                className={`${styles.bookingStatusBadge} ${
                                  booking.status === "completed"
                                    ? styles.statusCompleted
                                    : booking.status === "canceled"
                                    ? styles.statusCanceled
                                    : styles.statusPending
                                }`}
                              >
                                {booking.status === "completed"
                                  ? "Completed"
                                  : booking.status === "canceled"
                                  ? "Canceled"
                                  : "Pending"}
                              </span>
                            </div>

                            <div className={styles.bookingDetailsGrid}>
                              <p>
                                <strong>Service:</strong> {typeWithKind}
                              </p>
                              {booking.service_kind === "grip" && (
                                <p>
                                  <strong>Grip:</strong>{" "}
                                  {booking.service_name || "N/A"}
                                </p>
                              )}
                              {booking.service_kind === "string" && (
                                <p>
                                  <strong>String:</strong>{" "}
                                  {booking.is_hybrid === 1 || booking.is_hybrid ? (
                                    `Hybrid (Main: ${booking.string_name || booking.service_name || "BYOS"}${booking.string_brand ? ` [${booking.string_brand}]` : ""} / Cross: ${booking.string_name_cross || "BYOS"}${booking.string_brand_cross ? ` [${booking.string_brand_cross}]` : ""})`
                                  ) : (
                                    `${booking.string_name || booking.service_name || "N/A"}${booking.string_brand ? ` (${booking.string_brand})` : ""}`
                                  )}
                                </p>
                              )}
                              {booking.tension_type && (
                                <p>
                                  <strong>Tension:</strong> {formatTension(booking)}
                                </p>
                              )}
                              <p>
                                <strong>Cost:</strong>{" "}
                                <span className="dirham-symbol">&#xea;</span>{" "}
                                {parseFloat(booking.total_cost || 0).toFixed(2)}
                              </p>
                              {booking.odoo_receipt_number && (
                                <p>
                                  <strong>Odoo Receipt:</strong> {booking.odoo_receipt_number}
                                </p>
                              )}
                              <p>
                                <strong>Date:</strong>{" "}
                                {new Date(booking.booking_date).toLocaleDateString()}
                              </p>
                            </div>

                            {isBookingActive && (
                              <div className={styles.bookingActions}>
                                <button
                                  className={styles.smallCancelBtn}
                                  onClick={() => handleCancelBooking(booking)}
                                >
                                  Cancel Item
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {activeBookings.length > 0 && (
                    <div className={styles.groupActions}>
                      {group.bookings.length > 1 && !allActiveChecked && (
                        <span className={styles.selectionWarning}>
                          * Please check all pending items in this booking to complete.
                        </span>
                      )}
                      <button
                        className={styles.groupCompleteBtn}
                        disabled={group.bookings.length > 1 && !allActiveChecked}
                        onClick={() => handleMarkAsComplete(activeBookings)}
                      >
                        Mark Order Completed
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <p className={styles.message}>No bookings found.</p>
          )}

          {groupedBookings.length > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {showModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Complete Group Payment</h3>
            
            <p className={styles.modalSubtle} style={{ marginBottom: "15px" }}>
              Completing {modalBookings.length} booking(s) for {modalBookings[0]?.customer_name}
            </p>

            <div style={{
              backgroundColor: "#f9fafb",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "13px",
              marginBottom: "15px",
              border: "1px solid #e5e7eb"
            }}>
              <strong>Items to complete:</strong>
              <ul style={{ margin: "5px 0 0 0", paddingLeft: "20px", color: "#4b5563" }}>
                {modalBookings.map((b) => (
                  <li key={`${b.booking_id || b.grip_booking_id}-${b.service_kind}`}>
                    #{b.booking_id || b.grip_booking_id} — {b.racket_name || "Racket"} ({b.service_kind === "grip" ? "Grip" : "String"}): AED {parseFloat(b.total_cost || 0).toFixed(2)}
                  </li>
                ))}
              </ul>
            </div>

            <label className={styles.modalLabel}>
              Odoo Receipt Number
              <input
                type="text"
                className={styles.modalInput}
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
                disabled={saving}
                placeholder="Enter Odoo receipt code"
              />
            </label>

            <label className={styles.modalLabel}>
              Total Amount Paid (AED)
              <input
                type="number"
                step="0.01"
                min="0"
                className={styles.modalInput}
                value={cashAmount}
                onChange={(e) => setCashAmount(e.target.value)}
                disabled={saving}
              />
            </label>

            {formError && <p className={styles.error}>{formError}</p>}

            <div className={styles.modalActions}>
              <button
                className={styles.cancelBtn}
                onClick={closeModal}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className={styles.saveBtn}
                onClick={submitInStorePayment}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save & Mark Completed"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Cancel Item Booking</h3>
            {cancelBookingData && (
              <p className={styles.modalSubtle}>
                Are you sure you want to cancel booking #
                {cancelBookingData.booking_id ||
                  cancelBookingData.grip_booking_id}{" "}
                ({cancelBookingData.racket_name}) for {cancelBookingData.customer_name}?
              </p>
            )}

            <div className={styles.modalActions}>
              <button
                className={styles.cancelBtn}
                onClick={closeCancelModal}
                disabled={canceling}
              >
                No, Keep
              </button>
              <button
                className={styles.deleteBtn}
                onClick={confirmCancelBooking}
                disabled={canceling}
              >
                {canceling ? "Canceling…" : "Yes, Cancel Item"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCustomer;
